import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../../components/Topbar/Topbar";
import { loadGmHudConfig } from "../../utils/config";
import { logActivity } from "../../utils/activity";
import { fetchReports, fetchReport, STATUS_CATEGORY, formatWhen } from "./api";
import UploadQR from "./UploadQR";
import "./FaultReports.css";

// Resolved issues shown by default when "Vis også løste" is on; older ones on request only, so
// the list stays short as the project accumulates history.
const RESOLVED_WINDOW_DAYS = 60;

// Labels games-cloud puts on every issue: the category and the lower-cased location key.
const LOCATION_LABELS = { gothersgade: "GOTHERSGADE", vridslose: "VRIDSLOSE" };

function locationOf(issue) {
    for (const l of issue.labels || []) {
        if (LOCATION_LABELS[l]) return LOCATION_LABELS[l];
    }
    return "";
}

// The badge shows the Jira status by name — the FEJL project has its own Danish statuses (Ny,
// Undersøges, Afventer, Løst) — and takes its colour from the status category, so a renamed or
// added status still looks right. The category label is the fallback when Jira sends no name.
export function StatusBadge({ issue }) {
    const cat = STATUS_CATEGORY[issue.status_category] || STATUS_CATEGORY.new;
    return <span className={`fr-status ${cat.cls}`} title={cat.label}>{issue.status || cat.label}</span>;
}

// FaultReportsPage lists the maintenance tickets (Jira project FEJL) for the GM's locations with
// their current status, so a GM can see whether the broken prop is already known before filing a
// new one. Open to everyone on shift; the "Ny fejlmelding" button leads to the form.
export default function FaultReportsPage() {
    const navigate = useNavigate();
    const [cfg, setCfg] = useState(null);
    const [cfgLoaded, setCfgLoaded] = useState(false); // config attempt finished (null when games-cloud has no users sheet)
    const [locations, setLocations] = useState([]); // the user's location keys
    const [filterLoc, setFilterLoc] = useState("");
    const [showDone, setShowDone] = useState(false);
    const [doneDays, setDoneDays] = useState(RESOLVED_WINDOW_DAYS); // 0 = every resolved issue
    const [search, setSearch] = useState("");
    const [issues, setIssues] = useState(null); // null = loading
    const [truncated, setTruncated] = useState({ is: false, max: 0 });
    const [error, setError] = useState("");
    const [selected, setSelected] = useState(null); // { issue, photos }
    const [selectedKey, setSelectedKey] = useState("");

    useEffect(() => {
        logActivity("open_fault_reports");
        const email = (localStorage.getItem("userEmail") || "").toLowerCase();
        loadGmHudConfig().then(c => {
            setCfg(c);
            const locs = c?.users?.[email]?.locations || [];
            setLocations(locs);
            if (locs.length === 1) setFilterLoc(locs[0]);
            setCfgLoaded(true);
        });
    }, []);

    const load = useCallback(async () => {
        setError("");
        try {
            const { issues: list, truncated: isTruncated, max } = await fetchReports({ location: filterLoc || undefined, all: showDone, days: doneDays });
            setTruncated({ is: isTruncated, max });
            // Users with several locations get the unfiltered list; narrow it to theirs client-side.
            setIssues(filterLoc || locations.length === 0 ? list : list.filter(i => locations.includes(locationOf(i))));
        } catch (e) {
            setIssues([]);
            setError(/503/.test(e.message) ? "Fejlmeldinger er ikke sat op på serveren." : "Kunne ikke hente fejlmeldinger.");
        }
    }, [filterLoc, showDone, doneDays, locations]);

    useEffect(() => { if (cfgLoaded) load(); }, [cfgLoaded, load]);

    // Keep the list fresh while the page is open: games-cloud caches Jira for 60 s, so polling
    // every 30 s costs it nothing and a status change in Jira shows within about a minute.
    useEffect(() => {
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const open = useCallback(async (key) => {
        setSelectedKey(key);
        setSelected(null);
        try {
            setSelected(await fetchReport(key));
        } catch {
            setSelected({ error: true });
        }
    }, []);

    const locName = (key) => cfg?.locations?.[key]?.displayName || key;

    // The search box filters what is already loaded (room, summary, key, status) — instant, and
    // the lists are small enough that a round trip per keystroke would gain nothing.
    const needle = search.trim().toLowerCase();
    const visible = (issues || []).filter(i => !needle || [i.key, i.summary, i.status, ...(i.components || [])].join(" ").toLowerCase().includes(needle));

    return (
        <>
            <Topbar />
            <div className="fr-content">
                <div className="fr-head">
                    <div>
                        <h1>Fejlmeldinger</h1>
                        <p className="fr-sub">Kendte fejl på props, teknik og lokaler, og hvor langt de er.</p>
                    </div>
                    <button className="fr-primary" onClick={() => navigate("/fejlmeldinger/ny")}>+ Ny fejlmelding</button>
                </div>

                <div className="fr-filters">
                    {locations.length > 1 && (
                        <div className="fr-chips">
                            <button className={`fr-chip ${filterLoc === "" ? "active" : ""}`} onClick={() => setFilterLoc("")}>Alle</button>
                            {locations.map(l => (
                                <button key={l} className={`fr-chip ${filterLoc === l ? "active" : ""}`} onClick={() => setFilterLoc(l)}>{locName(l)}</button>
                            ))}
                        </div>
                    )}
                    <input
                        className="fr-search"
                        type="search"
                        placeholder="Søg i rum, overskrift eller nummer…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <label className="fr-toggle">
                        <input type="checkbox" checked={showDone} onChange={e => { setShowDone(e.target.checked); setDoneDays(RESOLVED_WINDOW_DAYS); }} />
                        Vis også løste
                    </label>
                </div>
                {showDone && (
                    <div className="fr-window">
                        {doneDays > 0
                            ? <>Løste sager fra de seneste {doneDays} dage. <button className="fr-linkbtn" onClick={() => setDoneDays(0)}>Vis alle løste</button></>
                            : <>Alle løste sager{truncated.is ? `, de ${truncated.max} nyeste` : ""}. <button className="fr-linkbtn" onClick={() => setDoneDays(RESOLVED_WINDOW_DAYS)}>Kun seneste {RESOLVED_WINDOW_DAYS} dage</button></>}
                    </div>
                )}

                {error && <div className="fr-error">{error}</div>}

                <div className="fr-layout">
                    <section className="fr-card fr-list-card">
                        {issues === null ? (
                            <div className="fr-empty">Henter…</div>
                        ) : visible.length === 0 ? (
                            <div className="fr-empty">{search ? "Ingen fejlmeldinger matcher søgningen." : showDone ? "Ingen fejlmeldinger." : "Ingen åbne fejlmeldinger. 🎉"}</div>
                        ) : (
                            <ul className="fr-list">
                                {visible.map(issue => (
                                    <li key={issue.key}>
                                        <button
                                            className={`fr-row ${issue.key === selectedKey ? "selected" : ""}`}
                                            onClick={() => open(issue.key)}
                                        >
                                            <div className="fr-row-main">
                                                <span className="fr-room">{(issue.components || [])[0] || "—"}</span>
                                                <span className="fr-summary">{issue.summary.replace(/^[^:]+:\s*/, "")}</span>
                                            </div>
                                            <div className="fr-row-meta">
                                                <StatusBadge issue={issue} />
                                                <span className="fr-key">{issue.key}</span>
                                                <span className="fr-when">{formatWhen(issue.created)}</span>
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <section className="fr-card fr-detail">
                        {!selectedKey ? (
                            <div className="fr-empty">Vælg en fejlmelding for at se detaljer og billeder.</div>
                        ) : selected === null ? (
                            <div className="fr-empty">Henter {selectedKey}…</div>
                        ) : selected.error ? (
                            <div className="fr-error">Kunne ikke hente {selectedKey}.</div>
                        ) : (
                            <ReportDetail data={selected} onRefresh={() => fetchReport(selectedKey).then(setSelected).catch(() => {})} />
                        )}
                    </section>
                </div>
            </div>
        </>
    );
}

export function ReportDetail({ data, onRefresh }) {
    const { issue, photos, comments = [] } = data;
    const [addingPhotos, setAddingPhotos] = useState(false);

    // While the QR is open, keep the photo list fresh so the GM sees the upload land.
    useEffect(() => {
        if (!addingPhotos || !onRefresh) return undefined;
        const t = setInterval(onRefresh, 4000);
        return () => clearInterval(t);
    }, [addingPhotos, onRefresh]);

    // A new issue selected → close the QR of the old one.
    useEffect(() => { setAddingPhotos(false); }, [issue.key]);

    return (
        <div className="fr-detail-body">
            <div className="fr-detail-head">
                <div>
                    <div className="fr-key">{issue.key} · {(issue.components || []).join(", ")}</div>
                    <h2>{issue.summary.replace(/^[^:]+:\s*/, "")}</h2>
                </div>
                <StatusBadge issue={issue} />
            </div>
            <div className="fr-meta-grid">
                <div><span className="fr-label">Prioritet</span>{issue.priority || "—"}</div>
                <div><span className="fr-label">Oprettet</span>{formatWhen(issue.created)}</div>
                <div><span className="fr-label">Opdateret</span>{formatWhen(issue.updated)}</div>
                <div><span className="fr-label">Kategori</span>{(issue.labels || []).filter(l => !["gothersgade", "vridslose"].includes(l)).join(", ") || "—"}</div>
            </div>
            <pre className="fr-description">{issue.description}</pre>
            <div className="fr-photos-head">
                <span className="fr-label">Billeder ({photos.length})</span>
                {issue.status_category !== "done" && (
                    <button className="fr-linkbtn" onClick={() => setAddingPhotos(v => !v)}>
                        {addingPhotos ? "Luk QR-koden" : "+ Tilføj billeder"}
                    </button>
                )}
            </div>
            {addingPhotos && <div className="fr-detail-qr"><UploadQR issueKey={issue.key} title={issue.summary.replace(/^[^:]+:\s*/, "")} /></div>}
            <PhotoGrid photos={photos} emptyText={addingPhotos ? "Venter på billeder fra telefonen…" : "Ingen billeder endnu."} />
            <div className="fr-photos-head fr-comments-head">
                <span className="fr-label">Kommentarer ({comments.length})</span>
            </div>
            {comments.length === 0 ? (
                <div className="fr-empty fr-empty-small">Ingen kommentarer endnu.</div>
            ) : (
                <ul className="fr-comments">
                    {comments.map(c => (
                        <li key={c.id} className="fr-comment">
                            <div className="fr-comment-meta">
                                <span className="fr-comment-author">{c.author || "Ukendt"}</span>
                                <span className="fr-when">{formatWhen(c.created)}</span>
                            </div>
                            <div className="fr-comment-body">{c.body}</div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export function PhotoGrid({ photos, emptyText = "Ingen billeder endnu." }) {
    if (!photos || photos.length === 0) return <div className="fr-empty fr-empty-small">{emptyText}</div>;
    return (
        <div className="fr-photos">
            {photos.map(p => (
                <a key={p.name} href={p.url} target="_blank" rel="noreferrer" className="fr-photo" title={formatWhen(p.uploaded)}>
                    <img src={p.url} alt={p.name} loading="lazy" />
                </a>
            ))}
        </div>
    );
}
